import { PIXEL_EMBLEMS, SPRITE_SIZE } from './pixelEmblems';
import { spritePaths, toneColors, unpackRuns } from './pixelPalette';

/**
 * The Long Night's emblems (docs/LONG_NIGHT_ART_DIRECTION.md §12.13): one object per item, as a
 * pixel sprite (pixelEmblems.ts, pixelled from game-icons.net glyphs by
 * scripts/art/pixel-emblems.ts). Every sprite is lit the same way (§5.2): the lamp catches its top
 * edges, its shadow leans toward violet, and a deep violet outline holds it against the smoke.
 * That one rule is what makes a brass bell and a crystal shard belong to the same world. Drawn as
 * SVG rectangles, so it stays crisp at every size and inside other SVG (the share card).
 */

const paths = new Map<string, readonly (readonly [number, string])[]>();

/** A sprite's paths, one per tone, worked out once. */
function pathsOf(name: string): readonly (readonly [number, string])[] {
  let p = paths.get(name);
  if (!p) {
    p = spritePaths(unpackRuns(PIXEL_EMBLEMS[name]![1]), SPRITE_SIZE);
    paths.set(name, p);
  }
  return p;
}

export type EmblemTone = 'lit' | 'silhouette';

export function Emblem({
  name,
  size = 40,
  tone = 'lit',
  className = '',
  title,
}: {
  /** An item's emblem key ('red-thread', 'fixture.strongbox', 'pledge.dice'…). */
  name: string;
  size?: number;
  /** A thing not yet found: an ink silhouette with a faint cold rim (§11.22). */
  tone?: EmblemTone;
  className?: string;
  title?: string;
}) {
  const key = PIXEL_EMBLEMS[name] ? name : 'unknown';
  const colors = toneColors(PIXEL_EMBLEMS[key]![0], tone === 'silhouette');
  return (
    <svg
      className={`emblem ${className}`}
      viewBox={`0 0 ${SPRITE_SIZE} ${SPRITE_SIZE}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {pathsOf(key).map(([t, d]) => (
        <path key={t} d={d} fill={colors[t]} />
      ))}
    </svg>
  );
}
